import {
  type PageTrackingLabel,
  type PageTrackingProps,
} from '@modules/utilities/pageBuild/types/PageProps';
import { NavigationTypeRaw } from '@modules/utilities/pageBuild/types/NavigationRaw';
import { engineApiTypeToTranslKey } from '@modules/apiReference/page/constants/apiReferenceConstants';
import {
  DocsSitePathSlug,
  LeadingSlashPathWithoutTrailingSlash,
} from '@modules/utilities/utils/path';

export type WithPageTracking<T> = T & {
  pageTrackingProps: PageTrackingProps;
};

export type PageTrackingItemData = PageTrackingProps & {
  description?: string;
  ignoreHash?: boolean; // if true, /page#hash is tracked as /page instead of /page#hash
  isTitleCode?: boolean; // if true, format title as code text
};

/**
 * Convert a URL or path to a trackable path for analytics.
 * - Extracts pathname from full URLs
 * - Strips hash fragments (we track pages, not sections)
 * - Removes '/docs' prefix for normalization
 *
 * Examples:
 *   'https://create.roblox.com/docs/cloud/features/accounts#/' → '/cloud/features/accounts'
 *   '/docs/en-us/reference/engine/Instance' → '/en-us/reference/engine/Instance'
 *   '/some/path#hash' → '/some/path'
 */
export const getTrackableUrlPath = (urlOrPath: string): LeadingSlashPathWithoutTrailingSlash => {
  // Extract pathname and strip hash
  let pathname: string;
  try {
    pathname = new URL(urlOrPath).pathname;
  } catch {
    // partial URL, e.g. /some/path#hash
    [pathname] = urlOrPath.split('#');
  }

  // Remove '/docs' prefix if present
  const parts = pathname.split('/').filter(Boolean); // filter out empty strings
  const docsIndex = parts.findIndex((part) => part === DocsSitePathSlug);

  if (docsIndex !== -1) {
    // If 'docs' found, take everything after it
    const pathParts = parts.slice(docsIndex + 1);
    return pathParts.length > 0 ? `/${pathParts.join('/')}` : '/';
  }

  // If no 'docs' found, return the path as-is (with leading slash)
  return parts.length > 0 ? `/${parts.join('/')}` : '/';
};

export function hasPageTrackingProps<T>(pageProps: T): pageProps is WithPageTracking<T> {
  return !!(
    pageProps &&
    typeof pageProps === 'object' &&
    'pageTrackingProps' in pageProps &&
    pageProps.pageTrackingProps
  );
}

const isApiContentType = (type: PageTrackingProps['type']) =>
  type === NavigationTypeRaw.CloudAPI ||
  type === NavigationTypeRaw.CloudAPI2 ||
  type === NavigationTypeRaw.CloudLegacy ||
  type === NavigationTypeRaw.CloudFeature ||
  type === NavigationTypeRaw.CloudDomain;

const defaultPageLabelByContentType = (
  type: PageTrackingProps['type'],
): PageTrackingLabel | undefined => {
  if (isApiContentType(type)) {
    return { translationKey: 'Label.API' };
  }
  if (type === NavigationTypeRaw.ReleaseNote) {
    return { translationKey: 'Heading.ReleaseNotes' };
  }
  // note: Markdown pages are determined by the type + path, not the type alone
  // see getMarkdownPageLabel
  return undefined;
};

// set on static props setting step
export const setPageTrackingProps = <T>(
  rawPageProps: T,
  title: PageTrackingProps['title'],
  type: PageTrackingProps['type'],
): WithPageTracking<T> => ({
  pageTrackingProps: {
    title,
    type,
    ...(() => {
      const defaultLabel = defaultPageLabelByContentType(type);
      return defaultLabel ? { label: defaultLabel } : {};
    })(),
  },
  ...rawPageProps,
});

// case-insensitive hashId matching
export const trackablePathMatch = (path: string) => path.toLowerCase();
export const isMatchingTrackablePaths = (path1: string, path2: string) =>
  trackablePathMatch(getTrackableUrlPath(path1)) === trackablePathMatch(getTrackableUrlPath(path2));

export const getEngineApiTypeTrackingLabel = (type: string): PageTrackingLabel => {
  return {
    translationKey:
      engineApiTypeToTranslKey[type as keyof typeof engineApiTypeToTranslKey] ?? 'Label.API',
  };
};

const getMarkdownPageLabel = (path: string): PageTrackingLabel => {
  // TODO: prioritize frontmatter label, if exists
  const pathSegments = path.replace(/#.*$/, '').toLowerCase().split('/');

  if (pathSegments.some((segment) => segment === 'tutorial' || segment === 'tutorials')) {
    return { translationKey: 'Label.Tutorial' };
  }

  return { translationKey: 'Label.Article' };
};

export const addPageTrackingDataByPath = (
  path: string,
  label: PageTrackingLabel,
  title: string,
  pageTrackingDataByHash: Record<string, Partial<PageTrackingItemData>>,
) => {
  return {
    ...pageTrackingDataByHash,
    [trackablePathMatch(path)]: {
      label,
      title,
    },
  };
};

export const getPageTrackingItemData = (
  pageProps: unknown,
  path: string,
  pageTrackingDataByHash: Record<string, Partial<PageTrackingItemData>>,
): PageTrackingItemData | null => {
  if (!hasPageTrackingProps(pageProps)) {
    return null;
  }
  let label =
    pageTrackingDataByHash[trackablePathMatch(path)]?.label ?? pageProps.pageTrackingProps.label;

  if (!label && pageProps.pageTrackingProps.type === NavigationTypeRaw.Markdown) {
    label = getMarkdownPageLabel(path);
  }

  // there is a 1-1 between having a label on the hash and hashes we track
  const ignoreHash = !label;
  return {
    ...pageProps.pageTrackingProps,
    ...pageTrackingDataByHash[trackablePathMatch(path)],
    label,
    title:
      pageTrackingDataByHash[trackablePathMatch(path)]?.title ?? pageProps.pageTrackingProps.title,
    ignoreHash,
  };
};
